import { useState, useEffect } from 'react';
import api from '../lib/api';

export interface CurrentElectionCycle {
  electionYear: number;
  name: string | null;
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  /** The cycle's state Blue Book link, when an admin has entered it. */
  blueBookUrl?: string | null;
}

interface UseCurrentElectionCycleReturn {
  cycle: CurrentElectionCycle | null;
  loading: boolean;
}

/**
 * Fetches the current election cycle window (start/end dates) for a state.
 * Returns null when no cycle window is configured (e.g. migration not yet
 * applied), so callers can disable cycle-based filtering gracefully.
 */
export function useCurrentElectionCycle(state = 'CO'): UseCurrentElectionCycleReturn {
  const [cycle, setCycle] = useState<CurrentElectionCycle | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/election-calendars/current?state=${encodeURIComponent(state)}`);
        if (cancelled) return;
        const d = response.data?.data;
        if (d && d.cycle_start_date && d.cycle_end_date) {
          setCycle({
            electionYear: Number(d.election_year),
            name: d.name || null,
            startDate: d.cycle_start_date,
            endDate: d.cycle_end_date,
            blueBookUrl: d.blue_book_url || null,
          });
        } else {
          setCycle(null);
        }
      } catch {
        if (!cancelled) setCycle(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [state]);

  return { cycle, loading };
}
